import toast from 'react-hot-toast'
import {
  ALERTS_ROUTE,
  ALERTS_CLOSE_ROUTE,
  ALERT_REPORT_ROUTE,
  ALERT_DETAILS_ROUTE,
  ALERT_SIMULATE_ROUTE,
} from '../../strings/api-consts'
import { Alert, AlertResponse, AlertSimType, ResolveAlert, ScanMethod } from '../../types/AlertTypes'
import { SelectOptionWithDarkMode } from '../../types/DropDownTypes'
import { setAlerts, setAlertTableCurrentPage, setTotalPages } from './alerts.slice'
import { ThreatHunterUsersResponse } from '../../types/SettingsType'
import { alertStatusOptions } from '../../strings/dropdown-options'
import { CustomThunkDispatch } from '../../types/UserTypes'
import Server from '../../api/server'
import { reportErrorToDatadog } from '../../utils/error-logging'
import { DEFAULT_TOAST_CONFIG } from '../../components/toast/consts'

const server = new Server()

export const fetchAlerts =
  (
    pageNumber: number,
    pageSize: number,
    statusFilter: SelectOptionWithDarkMode[],
    typeFilter: SelectOptionWithDarkMode[],
    threatHunterUsers: ThreatHunterUsersResponse | { error: string },
    sortOrder?: string,
    sortColumn?: string
  ) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = `${ALERTS_ROUTE}?page=${pageNumber}&pageSize=${pageSize}`

    if (statusFilter.length > 0 && statusFilter.length !== alertStatusOptions.length) {
      const statusParam = statusFilter.map((status) => `&status=${status.value}`).join('')
      url += statusParam
    }

    if (typeFilter.length > 0) {
      const typeParam = typeFilter.map((type) => `&alertType=${type.value}`).join('')
      url += typeParam
    }

    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    await server
      .get(url, dispatch)
      .then((res: AlertResponse) => {
        const updatedScans = res.alerts.map((alert) => {
          if (
            alert.scanMethod === ScanMethod.THREAT_HUNTER &&
            threatHunterUsers &&
            'threatHunterUsers' in threatHunterUsers
          ) {
            const threatHunterUser = threatHunterUsers.threatHunterUsers.find(
              (user) => user.accessCode === alert.scannedByAccessCode
            )
            if (threatHunterUser) {
              return { ...alert, scannedBy: threatHunterUser.email }
            }
          }
          return alert
        })
        dispatch(setAlertTableCurrentPage(!pageNumber ? 0 : pageNumber - 1))
        dispatch(setAlerts(updatedScans))
        dispatch(setTotalPages(res.totalPages))
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
      })
  }

export const closeAlerts = (alerts: ResolveAlert[]) => async (dispatch: CustomThunkDispatch) => {
  return server
    .post(
      ALERTS_CLOSE_ROUTE,
      alerts.map((alert) => {
        return {
          id: alert.alertId,
          scanId: alert.scanId,
          alertType: alert.alertType,
        }
      }),
      dispatch
    )
    .then((res) => {
      return res?.ok ?? false
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return false
    })
}

export const simulateAlert =
  (alert: AlertSimType, deviceIdentifier: string) => async (dispatch: CustomThunkDispatch) => {
    const url = `${ALERT_SIMULATE_ROUTE}`
    const alertEnumKey = Object.keys(AlertSimType).find(
      (key) => AlertSimType[key as keyof typeof AlertSimType] === alert
    )
    return server
      .post(url, { alert: alertEnumKey, deviceIdentifier: deviceIdentifier }, dispatch)
      .then((res) => {
        if (res?.status === 200) {
          return true
        } else {
          return false
        }
      })
      .catch((err) => {
        reportErrorToDatadog(err.message)
        return false
      })
  }

export const generateAndDownloadReport =
  (scanId: string, isThreatHunter: boolean, isDemo?: boolean) => (dispatch: CustomThunkDispatch) => {
    return new Promise((resolve, reject) => {
      const pollForReport = async () => {
        return server
          .post(
            ALERT_REPORT_ROUTE,
            { scanId: scanId, isThreatHunter: isThreatHunter, isDemo: isDemo ?? false },
            dispatch
          )
          .then((res) => {
            if (!res) {
              throw new Error('Response is undefined')
            }
            return res.json()
          })
          .then((res) => {
            if (res.message.includes('Report already exists')) {
              // Download the report
              const link = document.createElement('a')
              link.href = res.download_url
              link.download = ''
              link.style.display = 'none'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              toast.success('Report has been successfully generated', DEFAULT_TOAST_CONFIG)
              resolve(true)
            } else if (res.statusCode !== 200) {
              toast.error('There was an issue generating the report', DEFAULT_TOAST_CONFIG)
              reject(false)
            } else {
              setTimeout(pollForReport, 10000)
            }
          })
          .catch(() => {
            toast.error('There was an issue generating the report', DEFAULT_TOAST_CONFIG)
            reject(false)
          })
      }
      pollForReport()
    })
  }

export const fetchDetailsForAlert = (alertId: string) => async (dispatch: CustomThunkDispatch) => {
  const url = `${ALERT_DETAILS_ROUTE}`

  return server
    .post(url, { alertId: alertId }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((res: Alert) => {
      return res
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      return undefined
    })
}
